import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as naturalCompare from 'natural-compare-lite';
import api from '../../services/DataLayer';
import { sorter } from '../../utils/sortHelpers';
import { selectTitle } from './catalogsReducer';
import { selectPersonDetails } from './organizationReducer';

export const ImplementationStatus = Object.freeze({
  NOT_IMPLEMENTED: 'notImplemented',
  PARTIALLY_IMPLEMENTED: 'partiallyImplemented',
  FULLY_IMPLEMENTED: 'fullyImplemented',
  list() {
    return [this.NOT_IMPLEMENTED, this.PARTIALLY_IMPLEMENTED, this.FULLY_IMPLEMENTED];
  },
});

export const ApplicabilityReasons = Object.freeze({
  ABP: 'abp',
  BR: 'br',
  CC: 'cc',
  CO: 'co',
  LR: 'lr',
  list() {
    return Object.values(this).slice(0, -1);
  },
});

export const InapplicabilityReasons = Object.freeze({
  NRI: 'nri',
  NRO: 'nro',
  NRR: 'nrr',
  OTP: 'otp',
  list() {
    return Object.values(this).slice(0, -1);
  },
});

const ItemTypes = Object.freeze({
  CONTROL: 'control',
  GROUP: 'group',
  list() {
    return [this.CONTROL, this.GROUP];
  },
});

export const TypeCategories = Object.freeze({
  MANAGEMENT: 'management',
  OPERATIONAL: 'operational',
  list() {
    return [this.MANAGEMENT, this.OPERATIONAL];
  },
});

const initialState = {
  currentOrganization: '',
  customFrameworks: [],
  standardFrameworks: [],
  saveOnId: null,
  scrollReferenceId: null,
  scrollPosition: 0,
  controlItemInFocus: null,
  selectedFrameworkId: '',
  selectedGroupId: '',
  showInapplicable: true,
  showOnlyUnfinished: false,
  soas: [],
  unsavedChanges: false,
};

// Default values that apply to both management and operational controls
const controlDefaults = {
  activities: [],
  description: '',
  implementationStatus: ImplementationStatus.NOT_IMPLEMENTED,
  links: [],
  owner: [],
};

// Extra values that apply only to operational controls
const operationalControlDefaults = {
  applicable: true,
  applicabilityReasons: [],
  exclusionJustification: '',
};

const createControlObject = (id, type) => {
  const controlObject = {
    id,
    type,
    category: id.startsWith('ISO27002') ? TypeCategories.OPERATIONAL : TypeCategories.MANAGEMENT,
    ...(type === ItemTypes.CONTROL ? controlDefaults : {}),
    ...(id.startsWith('ISO27002') && type === ItemTypes.CONTROL ? operationalControlDefaults : {}),
  };
  return controlObject;
};

const isApplicabilitySet = (control) =>
  control.applicable
    ? control.applicabilityReasons.length > 0 || control.description?.trim().length > 0 || control.links.length > 0
    : control.applicabilityReasons.length > 0 ||
      control.exclusionJustification?.trim().length > 0 ||
      control.links.length > 0;

const isDirectSub = (item, screen) => {
  if (item.startsWith(screen)) {
    const ending = item.replace(screen, '').slice(1);
    // Direct sub must have more characters than just the screen and
    // not contain any dots (since then it's not direct sub)
    return ending.length > 0 && !ending.includes('.');
  }
  return false;
};

export const initializeStandardFrameworks = createAsyncThunk(
  'frameworks/initializeStandardFrameworks',
  async (_, { getState }) => {
    if (getState().frameworks.standardFrameworks.length === 0) {
      const response = await api.getStandardFrameworks();
      return response;
    }
    return undefined;
  },
);

export const initializeCustomFrameworks = createAsyncThunk(
  'frameworks/initializeCustomFrameworks',
  async (organizationId, { getState }) => {
    if (organizationId !== getState().frameworks.currentOrganization) {
      const response = await api.getCustomFrameworks(organizationId);
      return { response, organizationId };
    }
    return undefined;
  },
);

export const saveStandardFramework = createAsyncThunk('frameworks/saveStandardFramework', async (frameworkData) => {
  const frameworkId = await api.createStandardFramework(frameworkData);
  frameworkData.id = frameworkId;
  return frameworkData;
});

export const createCustomFramework = createAsyncThunk(
  'frameworks/createCustomFramework',
  async (frameworkFormData, { getState }) => {
    const frameworkData = {
      controls: Object.fromEntries(
        Object.entries(frameworkFormData.selectedControls).map(([k, v]) => [k, createControlObject(...v)]),
      ),
      description: frameworkFormData.description,
      title: frameworkFormData.title,
    };
    const frameworkId = await api.createCustomFramework(
      getState().organizations.selectedOrganization.id,
      frameworkData,
    );
    frameworkData.id = frameworkId;
    return frameworkData;
  },
);

// Triggered when controls unmount - id is control id
export const saveUnsavedChanges = createAsyncThunk('frameworks/saveUnsavedChanges', async (id, { getState }) => {
  const frameworkState = getState().frameworks;
  // When unsaved changes are pending and the caller is either deliberately empty or the one that is set in state, update the assessment
  if (frameworkState.unsavedChanges && (id === '' || id === frameworkState.saveOnId)) {
    const _ = await api.updateCustomFramework(
      getState().organizations.selectedOrganization.id,
      frameworkState.selectedFrameworkId,
      frameworkState.customFrameworks.find((fw) => fw.id === frameworkState.selectedFrameworkId),
    );
    return true;
  }
  return undefined;
});

// Triggered when changing selected framework / exiting edit mode - id is framework id
export const saveFramework = createAsyncThunk('frameworks/saveFramework', async (id, { getState }) => {
  const framework = getState().frameworks.customFrameworks.find((fw) => fw.id === id);
  const _ = await api.updateCustomFramework(getState().organizations.selectedOrganization.id, framework.id, framework);
  return true;
});

export const updateActivities = createAsyncThunk('frameworks/updateActivities', async (activityData, { getState }) => {
  const { selectedFrameworkId, currentOrganization, customFrameworks } = getState().frameworks;
  const framework = customFrameworks.find((fw) => fw.id === selectedFrameworkId);
  const newState = {
    ...framework,
    controls: Object.fromEntries(
      Object.values(framework.controls).map((control) => {
        let activities = [];
        if (control.type === ItemTypes.CONTROL) {
          if (activityData.controlIds.includes(control.id)) {
            activities = [...new Set([...control.activities, activityData.id])];
          } else {
            activities = control.activities.filter((activity) => activity !== activityData.id);
          }
        }
        return [control.id, { ...control, activities }];
      }),
    ),
  };
  const _ = await api.updateCustomFramework(currentOrganization, newState.id, newState);
  return newState;
});

export const saveSoa = createAsyncThunk('framework/saveSoa', async (completionPercentage, { getState }) => {
  try {
    const { currentOrganization, selectedFrameworkId, customFrameworks, soas } = getState().frameworks;
    const framework = customFrameworks.find((fw) => fw.id === selectedFrameworkId);
    const controls = Object.values(framework.controls)
      .filter((control) => control.type === ItemTypes.CONTROL && control.category === TypeCategories.OPERATIONAL)
      .sort(sorter);
    const { complete, unfinished } = Object.groupBy(controls, (control) =>
      isApplicabilitySet(control) ? 'complete' : 'unfinished',
    );
    const version = parseInt(soas.at(0)?.version ?? 0) + 1;
    const soa = {
      version,
      controls: complete || [],
      unfinishedControls: unfinished || [],
      completionPercentage,
      created: Date.now() / 1000,
    };
    await api.createSoa(currentOrganization, selectedFrameworkId, soa);
    return soa;
  } catch (error) {
    console.log(error);
    return undefined;
  }
});

export const fetchSoas = createAsyncThunk('framework/fetchSoas', async (fwId, { getState }) => {
  try {
    const organizationId = getState().frameworks.currentOrganization;
    const soas = await api.getSoas(organizationId, fwId);
    return soas;
  } catch (error) {
    return undefined;
  }
});

export const selectFramework = createAsyncThunk('frameworks/selectFramework', async (fwId, { getState, dispatch }) => {
  const { unsavedChanges, selectedFrameworkId } = getState().frameworks;
  if (unsavedChanges) {
    dispatch(saveFramework(selectedFrameworkId));
  }
  if (fwId !== '') {
    dispatch(fetchSoas(fwId));
  }
  return fwId;
});

// TODO: rework updating a custom framework
export const updateCustomFramework = createAsyncThunk(
  'frameworks/updateCustomFramework',
  async (frameworkData, { getState }) => {
    const _ = await api.updateCustomFramework(
      getState().organizations.selectedOrganization.id,
      frameworkData.id,
      frameworkData,
    );
    return frameworkData;
  },
);

export const frameworkSlice = createSlice({
  name: 'frameworks',
  initialState,
  reducers: {
    clearFrameworkSelection: (state, _) => {
      state.selectedFrameworkId = '';
    },
    clearScrollReference: (state, _) => {
      state.scrollReferenceId = null;
    },
    setScrollPosition: (state, action) => {
      state.scrollPosition = action.payload;
    },
    setControlItemInFocus: (state, action) => {
      state.controlItemInFocus = action.payload;
    },
    goToControl: (state, action) => {
      // check if payload is control -> select it & group is 1 level up
      // payload is group -> find first actual control
      let controlId;
      let groupId;
      const framework = state.customFrameworks.find((fw) => fw.id === state.selectedFrameworkId);
      if (framework.controls[action.payload].type === ItemTypes.GROUP) {
        const separator = action.payload.split(':').length > 2 ? '.' : '';
        const control = Object.values(framework.controls)
          .filter((item) => item.type === ItemTypes.CONTROL && item.id.startsWith(`${action.payload}${separator}`))
          .sort((a, b) => naturalCompare(a.id, b.id));
        controlId = control[0].id;
        groupId = action.payload;
      } else {
        groupId = action.payload.split('.').slice(0, -1).join('.');
        controlId = action.payload;
      }
      state.selectedGroupId = groupId;
      state.scrollReferenceId = controlId;
    },
    openGroup: (state, action) => {
      const framework = state.customFrameworks.find((fw) => fw.id === state.selectedFrameworkId);
      const controls = Object.values(framework.controls).sort(sorter);
      const controlType = action.payload === 'operational' ? TypeCategories.OPERATIONAL : TypeCategories.MANAGEMENT;
      const control = controls.find((c) => c.type === ItemTypes.CONTROL && c.category === controlType);
      const groupId = control?.id.split('.').slice(0, -1).join('.') || '';
      state.selectedGroupId = groupId;
      state.scrollReferenceId = control?.id || null;
    },
    selectGroup: (state, action) => {
      state.selectedGroupId = action.payload;
    },
    toggleInapplicable: (state, _) => {
      state.showInapplicable = !state.showInapplicable;
    },
    toggleOnlyUnfinished: (state, _) => {
      state.showOnlyUnfinished = !state.showOnlyUnfinished;
    },
    updateGroups: (state, action) => {
      const updatedFrameworks = state.customFrameworks.map((fw) =>
        fw.id !== state.selectedFrameworkId
          ? fw
          : {
              ...fw,
              controls: {
                ...fw.controls,
                [action.payload.id]: action.payload,
              },
            },
      );
      if (state.saveOnId === null) {
        state.saveOnId = action.payload.id; // Denotes the first edit caller to minimize update API calls
      }
      state.unsavedChanges = true;
      state.customFrameworks = updatedFrameworks;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createCustomFramework.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const customFrameworks = [...state.customFrameworks];
          customFrameworks.unshift(action.payload);
          state.customFrameworks = customFrameworks;
        }
      })
      .addCase(initializeStandardFrameworks.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.standardFrameworks = action.payload;
        }
      })
      .addCase(initializeCustomFrameworks.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.customFrameworks = action.payload.response;
          state.currentOrganization = action.payload.organizationId;
          state.saveOnId = null;
          state.scrollReferenceId = null;
          state.selectedFrameworkId = '';
          state.selectedGroupId = '';
          state.showInapplicable = true;
          state.unsavedChanges = false;
        }
      })
      .addCase(saveStandardFramework.fulfilled, (state, action) => {
        state.standardFrameworks = [...state.standardFrameworks, action.payload];
      })
      .addCase(saveUnsavedChanges.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.unsavedChanges = false;
          state.saveOnId = null; // Reset call id
        }
      })
      .addCase(saveFramework.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.unsavedChanges = false;
          state.saveOnId = null; // Reset call id
        }
      })
      .addCase(updateActivities.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newState = [...state.customFrameworks];
          const index = newState.findIndex((fw) => fw.id === action.payload.id);
          newState[index] = action.payload;
          state.customFrameworks = newState;
          state.unsavedChanges = false;
          state.saveOnId = null; // Reset call id
        }
      })
      .addCase(saveSoa.fulfilled, (state, action) => {
        state.soas = [action.payload, ...state.soas];
      })
      .addCase(fetchSoas.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          state.soas = action.payload;
        } else {
          state.soas = [];
        }
      })
      .addCase(selectFramework.fulfilled, (state, action) => {
        state.selectedFrameworkId = action.payload;
        state.scrollReferenceId = null;
        state.selectedGroupId = '';
      })
      .addCase(updateCustomFramework.fulfilled, (state, action) => {
        if (action.payload !== undefined) {
          const newState = [...state.customFrameworks];
          const index = newState.findIndex((fw) => fw.id === action.payload.id);
          newState[index] = action.payload;
          state.customFrameworks = newState;
        }
      })
      .addCase('resetState', (_, __) => ({ ...initialState }));
  },
});

const { actions, reducer } = frameworkSlice;

export const {
  clearFrameworkSelection,
  clearScrollReference,
  setScrollPosition,
  setControlItemInFocus,
  goToControl,
  openGroup,
  selectGroup,
  toggleInapplicable,
  toggleOnlyUnfinished,
  updateGroups,
} = actions;

export default reducer;

export const selectScrollReferenceId = (state) => state.frameworks.scrollReferenceId;

export const selectScrollPosition = (state) => state.frameworks.scrollPosition;

export const selectControlItemInFocus = (state) => state.frameworks.controlItemInFocus;

export const selectStandardFrameworks = createSelector(
  [(state) => state.frameworks.standardFrameworks],
  (standardFws) =>
    standardFws.map((framework) => {
      const fwDetails = {
        id: framework.id,
        title: framework.title,
      };
      return fwDetails;
    }) || [],
);

export const selectCustomFrameworks = createSelector(
  [(state) => state.frameworks.customFrameworks],
  (customFws) =>
    customFws.map((framework) => {
      const fwDetails = {
        id: framework.id,
        title: framework.title,
      };
      return fwDetails;
    }) || [],
);

const selectAllFrameworks = createSelector(
  [(state) => state.frameworks.standardFrameworks, (state) => state.frameworks.customFrameworks],
  (standardList, customList) => [...standardList, ...customList],
);

export const selectSelectedFrameworkId = (state) => state.frameworks.selectedFrameworkId;

export const selectFrameworkData = createSelector([selectAllFrameworks, (_, fwId) => fwId], (frameworkList, fwId) =>
  frameworkList.find((fw) => fw.id === fwId),
);

export const selectSelectedFramework = createSelector(
  selectAllFrameworks,
  selectSelectedFrameworkId,
  (frameworks, frameworkId) => frameworks.find((fw) => fw.id === frameworkId),
);

export const selectSelectedFrameworkTitle = createSelector(
  selectAllFrameworks,
  selectSelectedFrameworkId,
  (frameworks, frameworkId) => frameworks.find((fw) => fw.id === frameworkId).title,
);

const selectSelectedFrameworkEntities = createSelector(selectSelectedFramework, (framework) =>
  Object.values(framework?.controls ?? {}).sort(sorter),
);

export const selectFrameworkTitle = createSelector([selectAllFrameworks, (_, fwId) => fwId], (frameworkList, fwId) =>
  frameworkList.find((fw) => fw.id === fwId) ? frameworkList.find((fw) => fw.id === fwId).title : 'Framework not found',
);

export const selectFrameworkIds = createSelector(selectAllFrameworks, (frameworkList) =>
  frameworkList.map((fw) => fw.id),
);

export const selectSelectedGroups = createSelector(selectSelectedFrameworkEntities, (controls) =>
  controls.filter((c) => c.type === ItemTypes.GROUP),
);

export const selectSelectedControls = createSelector(selectSelectedFrameworkEntities, (controls) =>
  controls.filter((c) => c.type === ItemTypes.CONTROL),
);

export const selectControlGroups = createSelector(
  [selectSelectedGroups, (_, category) => category],
  (groups, category) => groups.filter((group) => group.category === category),
);

export const selectCatalogGroups = createSelector(
  [selectSelectedGroups, (_, category) => category],
  (groups, category) => groups.filter((group) => group.category === category && group.id.split(':').length === 2),
);

export const selectShowInapplicable = (state) => state.frameworks.showInapplicable;
export const selectShowOnlyUnfinished = (state) => state.frameworks.showOnlyUnfinished;

export const selectSubControls = createSelector(
  [selectSelectedFrameworkEntities, (_, groupId) => groupId],
  (controls, groupId) => controls.filter((item) => isDirectSub(item.id, groupId)),
);

const includeControl = (control, showInapplicable, showOnlyUnfinished) =>
  (showInapplicable || control.applicable) && !(showOnlyUnfinished && isApplicabilitySet(control));

const selectFilteredControls = createSelector(
  [selectSelectedControls, selectShowInapplicable, selectShowOnlyUnfinished],
  (controls, showInapplicable, showOnlyUnfinished) =>
    controls.filter(
      (control) =>
        control.category === TypeCategories.OPERATIONAL &&
        (showInapplicable || control.applicable) &&
        !(showOnlyUnfinished && isApplicabilitySet(control)),
    ),
);

export const selectSoaPdfData = createSelector([(state, soa) => soa, (state) => state], (soa, state) => {
  if (!soa) {
    return [];
  }

  const controls = soa.controls.map((control) => {
    const title = selectTitle(state, control.id);
    const ownerNames = control.owner
      .map((ownerId) => {
        const ownerDetails = selectPersonDetails(state, ownerId);
        if (!ownerDetails.name && (!ownerDetails.firstName || !ownerDetails.lastName)) {
          return '';
        }
        return ownerDetails.name ? ownerDetails.name : `${ownerDetails.firstName} ${ownerDetails.lastName}`;
      })
      .filter((name) => name);
    return {
      ...control,
      title,
      owner: ownerNames,
    };
  });

  const unfinishedControls = soa.unfinishedControls.map((control) => {
    const title = selectTitle(state, control.id);
    return { ...control, title };
  });

  return { controls, unfinishedControls };
});

export const selectFilteredSubControls = createSelector(
  [
    selectSelectedFrameworkEntities,
    selectFilteredControls,
    selectShowInapplicable,
    selectShowOnlyUnfinished,
    (_, groupId) => groupId,
  ],
  (entities, controls, showInapplicable, showOnlyUnfinished, groupId) => {
    let subControls = entities.filter((item) => isDirectSub(item.id, groupId));
    if (!showInapplicable || showOnlyUnfinished) {
      subControls = subControls.filter(
        (item) =>
          (item.type === ItemTypes.GROUP && controls.some((control) => control.id.startsWith(item.id))) ||
          (item.type === ItemTypes.CONTROL && includeControl(item, showInapplicable, showOnlyUnfinished)),
      );
    }
    return subControls;
  },
);

export const selectSelectedGroupId = (state) => state.frameworks.selectedGroupId;

export const selectSelectedGroupControls = createSelector(
  [selectSelectedControls, selectSelectedGroupId, (_, category) => category],
  (controls, groupId, category) => controls.filter((c) => c.category === category && c.id.startsWith(groupId)),
);

export const selectFilteredSelectedGroupControls = createSelector(
  [selectSelectedControls, selectSelectedGroupId, selectShowInapplicable, selectShowOnlyUnfinished],
  (controls, groupId, showInapplicable, showOnlyUnfinished) =>
    controls.filter(
      (item) =>
        item.category === TypeCategories.OPERATIONAL &&
        item.id.startsWith(groupId) &&
        includeControl(item, showInapplicable, showOnlyUnfinished),
    ),
);

// drops out non-applicable controls from operational controls
export const selectControlCount = createSelector(
  [selectSelectedControls, (_, category) => category],
  (controls, category) =>
    controls.filter((control) => control.category === category && (control.applicable ?? true)).length ?? 0,
);

// drops out non-applicable controls from operational controls
export const selectTotalControlCount = createSelector([selectAllFrameworks, (_, fwId) => fwId], (frameworks, fwId) => {
  const framework = frameworks.find((fw) => fw.id === fwId);
  const controls = Object.values(framework?.controls ?? {});
  return controls.filter((control) => control.type === ItemTypes.CONTROL && (control.applicable ?? true)).length ?? 0;
});

export const selectImplementationStatus = createSelector(
  [selectSelectedControls, (_, category) => category],
  (controls, category) => {
    const controlsByCategory = controls.filter(
      (control) => control.category === category && (control.applicable ?? true),
    );
    const controlsByStatus = Object.groupBy(controlsByCategory, (obj) => obj.implementationStatus);
    return Object.fromEntries(
      ImplementationStatus.list().map((status) => [status, controlsByStatus[status]?.length ?? 0]),
    );
  },
);

export const selectTotalImplementationStatus = createSelector(
  [selectAllFrameworks, (_, fwId) => fwId],
  (frameworks, fwId) => {
    const framework = frameworks.find((fw) => fw.id === fwId);
    const controls = Object.values(framework?.controls ?? {}).filter(
      (control) => control.type === ItemTypes.CONTROL && (control.applicable ?? true),
    );
    const controlsByStatus = Object.groupBy(controls, (obj) => obj.implementationStatus);
    return Object.fromEntries(
      ImplementationStatus.list().map((status) => [status, controlsByStatus[status]?.length ?? 0]),
    );
  },
);

export const selectApplicabilityStatus = createSelector(selectSelectedControls, (controls) => {
  const opControls = controls.filter((control) => control.category === TypeCategories.OPERATIONAL);
  const completed = opControls.filter((control) => isApplicabilitySet(control));
  return opControls.length > 0 ? Math.round((completed.length / opControls.length) * 1000) / 10 : '-';
});

export const selectSoas = (state) => state.frameworks.soas;

export const selectSelectedFrameworkControlIds = createSelector(selectSelectedControls, (controls) =>
  controls.map((control) => control.id).sort((a, b) => naturalCompare(a.id, b.id)),
);

export const selectSoaControls = createSelector(
  [selectSelectedFrameworkEntities, (_, groupId) => groupId],
  (entities, groupId) => {
    const operationalControls = entities.filter((control) => isDirectSub(control.id, groupId));
    const controls = operationalControls.map((control) => {
      let rVal = control;
      if (control.type === ItemTypes.CONTROL) {
        rVal = {
          ...control,
          finished: isApplicabilitySet(control),
        };
      }
      return rVal;
    });
    return controls.sort(sorter);
  },
);

export const selectAreAllControlsComplete = createSelector([(state) => state], (state) => {
  const checkControlsComplete = (id) => {
    const groupControls = selectSoaControls(state, id);
    return groupControls.every((control) => {
      if (control.type === ItemTypes.GROUP) {
        return checkControlsComplete(control.id);
      }
      return control.finished;
    });
  };
  const catalogGroups = selectCatalogGroups(state, TypeCategories.OPERATIONAL);
  return catalogGroups.every((catalog) => checkControlsComplete(catalog.id));
});
